import { PhaseStrategy } from '../types/phaseStrategy';
import React from 'react';

interface StrategyComponent {
  component: React.ComponentType<any>;
  capabilities: string[];
  config?: any;
}

class StrategyRegistry {
  private strategies: Map<string, PhaseStrategy> = new Map();
  private componentMap: Map<string, StrategyComponent> = new Map();
  
  async initialize() {
    try {
      // Import the phaseStrategyService
      const { getPhaseStrategies } = await import('./phaseStrategyService');
      
      // Fetch strategies from backend
      const strategies = await getPhaseStrategies();
      
      // Register strategies
      strategies.forEach(strategy => {
        this.strategies.set(strategy.type, strategy);
      });
      
      // Register default components
      this.registerDefaultComponents();
      
      console.log('Strategy registry initialized with', strategies.length, 'strategies');
    } catch (error) {
      console.error('Failed to initialize strategy registry:', error);
      // Register default components even if API fails
      this.registerDefaultComponents();
    }
  }
  
  registerComponent(strategyType: string, component: React.ComponentType<any>, capabilities: string[] = []) {
    this.componentMap.set(strategyType, { 
      component, 
      capabilities,
      config: this.strategies.get(strategyType)?.config_schema
    });
  }
  
  async getComponent(strategyType: string): Promise<React.ComponentType<any>> {
    // Try to find a registered component for this phase type
    const component = this.componentMap.get(strategyType)?.component;
    
    console.log(`getComponent(${strategyType}):`, { 
      found: !!component, 
      registeredTypes: Array.from(this.componentMap.keys()) 
    });
    
    if (component) {
      return component;
    }
    
    // If component not found, try to load it dynamically
    try {
      // Map common phase types to their components if not registered
      if (strategyType === 'team_selection') {
        console.log('Dynamically importing TeamSelectionPhase');
        const { TeamSelectionPhase } = await import('../components/phases/TeamSelectionPhase');
        console.log('TeamSelectionPhase imported successfully:', TeamSelectionPhase);
        this.registerComponent('team_selection', TeamSelectionPhase);
        return TeamSelectionPhase;
      }
    
      if (strategyType === 'brainstorming') {
        const { BrainstormingPhase } = await import('../components/phases/BrainstormingPhase');
        this.registerComponent('brainstorming', BrainstormingPhase);
        return BrainstormingPhase;
      }
      
      if (strategyType === 'critique') {
        const { CritiquePhase } = await import('../components/phases/CritiquePhase');
        this.registerComponent('critique', CritiquePhase);
        return CritiquePhase;
      }
      
      if (strategyType === 'synthesis') {
        const { SynthesisPhase } = await import('../components/phases/SynthesisPhase');
        this.registerComponent('synthesis', SynthesisPhase);
        return SynthesisPhase;
      }
      
      if (strategyType === 'refinement') {
        const { RefinementPhase } = await import('../components/phases/RefinementPhase');
        this.registerComponent('refinement', RefinementPhase);
        return RefinementPhase;
      }
      
      // If no specific component is found, use the DefaultPhase component
      return this.getDefaultComponent();
    } catch (error) {
      console.error(`Error loading component for ${strategyType}:`, error);
      // Return a loading component that will retry after a delay
      return this.getRetryComponent(strategyType);
    }
  }
  
  getStrategy(strategyType: string): PhaseStrategy | undefined {
    return this.strategies.get(strategyType);
  }
  
  getAllStrategies(): PhaseStrategy[] {
    return Array.from(this.strategies.values());
  }
  
  private registerDefaultComponents() {
    // Import default components
    import('../components/phases/DefaultPhase').then(({ DefaultPhase }) => {
      // Register a default component for unknown strategies
      this.componentMap.set('default', { 
        component: DefaultPhase,
        capabilities: []
      });
    }).catch(() => {
      console.log('DefaultPhase component not available, using fallback');
      // If even the default component isn't available, use a simple fallback
      this.componentMap.set('default', { 
        component: (props: any) => React.createElement('div', {}, `No component registered for strategy: ${props.phase}`),
        capabilities: []
      });
    });
    
    // Register specific components when they become available
    // This would be expanded as phase-specific components are implemented
    import('../components/phases/TeamSelectionPhase').then(({ TeamSelectionPhase }) => {
      this.registerComponent('team_selection', TeamSelectionPhase, ['auto_select', 'manual_select']);
    }).catch(() => {
      console.log('TeamSelectionPhase component not available, using default');
    });
    
    import('../components/phases/BrainstormingPhase').then(({ BrainstormingPhase }) => {
      this.registerComponent('brainstorming', BrainstormingPhase, ['idea_generation', 'parallel_processing']);
    }).catch(() => {
      console.log('BrainstormingPhase component not available, using default');
    });
    
    // Register additional phase components
    import('../components/phases/CritiquePhase').then(({ CritiquePhase }) => {
      this.registerComponent('critique', CritiquePhase, ['evaluation', 'feedback']);
    }).catch(() => {
      console.log('CritiquePhase component not available, using default');
    });
    
    import('../components/phases/SynthesisPhase').then(({ SynthesisPhase }) => {
      this.registerComponent('synthesis', SynthesisPhase, ['solution_generation', 'integration']);
    }).catch(() => {
      console.log('SynthesisPhase component not available, using default');
    });
    
    import('../components/phases/RefinementPhase').then(({ RefinementPhase }) => {
      this.registerComponent('refinement', RefinementPhase, ['editing', 'polishing', 'versioning']);
    }).catch(() => {
      console.log('RefinementPhase component not available, using default');
    });
  }
  
  private getDefaultComponent(): React.ComponentType<any> {
    const DefaultComponent = this.componentMap.get('default')?.component;
    if (!DefaultComponent) {
      // If even the default component isn't registered, return a simple fallback
      return (props: any) => React.createElement('div', {}, `No component registered for strategy: ${props.phase}`);
    }
    return DefaultComponent;
  }
  
  private getRetryComponent(strategyType: string): React.ComponentType<any> {
    return class RetryComponent extends React.Component<any, { retrying: boolean, retryCount: number }> {
      private retryTimeout: NodeJS.Timeout | null = null;
      
      constructor(props: any) {
        super(props);
        this.state = { retrying: false, retryCount: 0 };
      }
      
      componentDidMount() {
        this.scheduleRetry();
      }
      
      componentWillUnmount() {
        if (this.retryTimeout) {
          clearTimeout(this.retryTimeout);
        }
      }
      
      scheduleRetry() {
        if (this.state.retryCount < 5) { // Limit to 5 retries
          this.setState({ retrying: true });
          
          // Exponential backoff: 1s, 2s, 4s, 8s, 16s
          const delay = Math.pow(2, this.state.retryCount) * 1000;
          
          this.retryTimeout = setTimeout(() => {
            this.setState(prevState => ({ 
              retryCount: prevState.retryCount + 1,
              retrying: false
            }));
            
            // Force re-render which will trigger another getComponent call
            this.forceUpdate();
          }, delay);
        }
      }
      
      render() {
        if (this.state.retrying) {
          return React.createElement('div', { className: 'p-4 text-center' }, 
            `Loading ${strategyType} component... (Attempt ${this.state.retryCount + 1}/5)`
          );
        }
        
        // Try to get the component again
        const registry = strategyRegistry;
        registry.getComponent(strategyType)
          .then(Component => {
            // If we successfully got the component, force a re-render
            this.forceUpdate();
          })
          .catch(() => {
            // If it failed again, schedule another retry
            this.scheduleRetry();
          });
        
        return React.createElement('div', { className: 'p-4 text-center' }, 
          `Loading ${strategyType} component... (Attempt ${this.state.retryCount + 1}/5)`
        );
      }
    };
  }
}

// Create a singleton instance
const strategyRegistry = new StrategyRegistry();

export default strategyRegistry;
